// Builds the policy details model from the policy's `policyType` by
// looking up the form fields the input form would render
// (`getPolicyFormFields`) and pulling the corresponding values off the
// policy. Pure data-transformation — no React imports.

import {
  getMaxDailyBenefitAmount,
  getMaxMonthlyBenefitAmount,
  isIndependentLivingBenefitZeroedOut,
} from '@shared/calculations/funding/projection/sharedPolicyConfig';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { FundingPolicyType } from '@shared/appModel';
import { getPolicyFormFields, FORM_FIELD_COMPONENTS } from '@/components/InsuranceFormFields';
import { fundingPolicyTypeLabel } from '../../glossary/enumLabels';
import { formatValue } from '../../glossary/formatValue';
import type { DescriptorContext, DescriptorText } from './formFieldDescriptors';
import { descriptorsForFormFields } from './formFieldDescriptors';
import { EnvRow, FieldSpec } from './types';

export interface BuildPolicyDetailsInput {
  policy: PolicyFundingSource | null;
  /**
   * `clientHasStartedLtc` from the intake survey. When true, the form
   * also renders `BenefitUtilizedToDateField`; we mirror that here.
   */
  clientHasStartedLtc: boolean;
}

export interface PolicyDetailsModel {
  identityFields: FieldSpec[];
  formFields: FieldSpec[];
  envRows: EnvRow[];
  hasPerEnv: boolean;
  /**
   * True when the calc-side carve-out zeroes the independent living
   * benefit even though the policy carries a non-zero stored value —
   * i.e. the form shows a value the projection will never pay.
   */
  independentLivingZeroedOut: boolean;
}

function buildIdentityFields(policy: PolicyFundingSource | null): FieldSpec[] {
  if (!policy) return [];
  const carrier = (policy.sourceQuote as { carrierName?: string } | undefined)?.carrierName ?? null;
  return [
    { label: 'Custom name', value: policy.policyCustomName },
    { label: 'Carrier', value: carrier },
    {
      label: 'Policy type',
      value: fundingPolicyTypeLabel(policy.policyType as unknown as number),
      format: 'text',
    },
  ];
}

function buildEnvRows(p: PolicyFundingSource): EnvRow[] {
  // The edit form can display these as daily/monthly/annual via runtime
  // form state. Policy Details has only the persisted policy, so it shows
  // the canonical stored daily amounts.
  return [
    {
      env: 'Home',
      daily: p.dailyHomeBenefitAmount,
      inflation: p.dailyHomeInflationProtectionPercent,
    },
    {
      env: 'Independent living',
      // Show the stored value as entered, like the other care settings.
      // When the shop-insurance reimbursement carve-out zeroes IL in
      // projections, the warning above the table explains the discrepancy
      // (see independentLivingZeroedOut).
      daily: p.dailyIndependentLivingBenefitAmount,
      inflation: p.dailyIndependentLivingInflationProtectionPercent,
    },
    {
      env: 'Assisted living',
      daily: p.dailyAssistedLivingBenefitAmount,
      inflation: p.dailyAssistedLivingInflationProtectionPercent,
    },
    {
      env: 'Nursing home',
      daily: p.dailyNursingHomeBenefitAmount,
      inflation: p.dailyNursingHomeInflationProtectionPercent,
    },
  ];
}

function assetFlexYearOnePoolFields(policy: PolicyFundingSource): FieldSpec[] {
  if (policy.policyType !== FundingPolicyType.nylAssetFlex) return [];

  const monthlyBenefit = getMaxMonthlyBenefitAmount(policy);
  const dailyBenefit = getMaxDailyBenefitAmount(policy);
  const accelMonths = policy.policyAccelerationBenefitDurationMonths;
  const eobMonths = policy.policyEobBenefitDurationMonths;
  const aobValue =
    accelMonths != null ? monthlyBenefit * accelMonths : policy.policyMaximumBenefitAmount;
  const eobValue =
    eobMonths != null ? monthlyBenefit * eobMonths : policy.policyContinuationBenefitAmount;
  const durationTooltip = (
    months: number | null | undefined,
    durationLabel: string,
    fallbackLabel: string,
  ) =>
    months != null
      ? `Calculated value: highest daily benefit (${formatValue(dailyBenefit, 'currency')}) x ${DAYS_IN_MONTH} days x ${formatValue(months, 'months')} ${durationLabel}.`
      : `Displayed from the entered ${fallbackLabel} because no ${durationLabel} is set.`;

  return [
    {
      label: 'Year-1 Acceleration (AOB)',
      value: aobValue,
      format: 'currency',
      tooltip: durationTooltip(accelMonths, 'acceleration duration', 'face amount'),
    },
    {
      label: 'Year-1 Extension (EOB)',
      value: eobValue,
      format: 'currency',
      tooltip: durationTooltip(eobMonths, 'EOB duration', 'EOB amount'),
    },
  ];
}

function adjustAssetFlexReferenceFields(
  fields: FieldSpec[],
  policy: PolicyFundingSource,
): FieldSpec[] {
  if (policy.policyType !== FundingPolicyType.nylAssetFlex) return fields;

  const eobDurationSet = policy.policyEobBenefitDurationMonths != null;
  return fields
    .filter(field => !(field.label === 'EOB amount' && eobDurationSet))
    .map(field => {
      if (field.label === 'Face amount') {
        return {
          ...field,
          label: 'Face amount (DB)',
          tooltip: undefined,
        };
      }
      return field;
    });
}

function resolveDescriptorText(
  text: DescriptorText | undefined,
  context: DescriptorContext,
): string | undefined {
  return typeof text === 'function' ? text(context) : text;
}

export function buildPolicyDetails(opts: BuildPolicyDetailsInput): PolicyDetailsModel {
  const { policy, clientHasStartedLtc } = opts;
  const identityFields = buildIdentityFields(policy);

  if (!policy?.policyType) {
    return {
      identityFields,
      formFields: [],
      envRows: [],
      hasPerEnv: false,
      independentLivingZeroedOut: false,
    };
  }

  const formComponents = [
    ...(clientHasStartedLtc ? [FORM_FIELD_COMPONENTS.BenefitUtilizedToDateField] : []),
    ...getPolicyFormFields(policy.policyType),
  ];
  const descriptors = descriptorsForFormFields(formComponents);
  const descriptorContext: DescriptorContext = { policy, clientHasStartedLtc };

  let formFields: FieldSpec[] = [];
  let hasPerEnv = false;
  for (const group of descriptors) {
    if (group.kind === 'perEnv') {
      hasPerEnv = true;
      continue;
    }
    for (const f of group.fields) {
      const rawValue = f.getValue
        ? f.getValue(descriptorContext)
        : f.key
          ? policy[f.key]
          : undefined;
      const value =
        f.valueMultiplier && typeof rawValue === 'number' ? rawValue * f.valueMultiplier : rawValue;
      formFields.push({
        label: resolveDescriptorText(f.label, descriptorContext) ?? '',
        tooltip: resolveDescriptorText(f.tooltip, descriptorContext),
        value,
        format: f.format,
      });
    }
  }
  formFields = [
    ...assetFlexYearOnePoolFields(policy),
    ...adjustAssetFlexReferenceFields(formFields, policy),
  ];

  return {
    identityFields,
    formFields,
    envRows: hasPerEnv ? buildEnvRows(policy) : [],
    hasPerEnv,
    independentLivingZeroedOut:
      hasPerEnv &&
      isIndependentLivingBenefitZeroedOut(policy) &&
      (policy.dailyIndependentLivingBenefitAmount ?? 0) > 0,
  };
}
